import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../../lib/api';
import { BarChart3, Eye, Users, TrendingUp } from 'lucide-react';

const Stat = ({ icon: Icon, label, value, color, testId }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={testId}>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20`, color }}>
        <Icon size={20} />
      </div>
      <div className="text-sm text-[#64748B]">{label}</div>
    </div>
    <div className="text-3xl font-bold text-[#2D3748]">{value}</div>
  </div>
);

const DailyChart = ({ data }) => {
  const max = Math.max(1, ...data.map(d => d.unique_visitors));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <h3 className="font-semibold text-[#2D3748] mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-[#4ECDC4]" /> Návštěvy za posledních {data.length} dní</h3>
      <div className="flex items-end gap-1 h-48 overflow-x-auto pb-2" data-testid="analytics-bars">
        {data.map((d) => {
          const heightPct = (d.unique_visitors / max) * 100;
          const date = new Date(d.date);
          const label = `${date.getDate()}.${date.getMonth() + 1}.`;
          return (
            <div key={d.date} className="flex flex-col items-center gap-1 flex-shrink-0" style={{ minWidth: '24px' }}>
              <div className="text-xs text-[#64748B] font-medium">{d.unique_visitors || ''}</div>
              <div className="w-5 rounded-t-md bg-gradient-to-t from-[#4ECDC4] to-[#95E1D3] hover:from-[#3DBDB5] transition-colors relative group" style={{ height: `${Math.max(2, heightPct)}%`, minHeight: '4px' }} title={`${label} — ${d.unique_visitors} návštěvníků, ${d.total_hits} zobrazení`}>
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#2D3748] text-white text-xs rounded-lg px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {label}: {d.unique_visitors} návštěvníků
                </div>
              </div>
              <div className="text-[10px] text-[#94A3B8] -rotate-45 origin-top-left mt-1 w-0">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PRESETS = [
  { value: 7, label: '7 dní' },
  { value: 30, label: '30 dní' },
  { value: 90, label: '90 dní' }
];

export const AnalyticsAdmin = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    analyticsAPI.stats(days)
      .then(res => { setStats(res.data); setError(null); })
      .catch(e => setError(e?.response?.status === 404 ? 'Backend ještě nemá tuto funkci. Redeployujte produkci.' : 'Nepodařilo se načíst statistiky'))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50] flex items-center gap-2"><TrendingUp className="text-[#4ECDC4]" size={26} /> Statistika návštěvnosti</h1>
          <p className="text-[#64748B]">Anonymní přehled návštěv webu. Žádné cookies, žádné osobní údaje — vidíte pouze počty.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          {PRESETS.map(p => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${days === p.value ? 'bg-white text-[#2D3748] shadow' : 'text-[#64748B] hover:text-[#2D3748]'}`}
              data-testid={`analytics-range-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex items-center justify-center min-h-[30vh]"><div className="spinner" /></div>}
      {error && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">{error}</div>}

      {stats && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Users} label="Unikátní návštěvníci" value={stats.unique_visitors} color="#4ECDC4" testId="stat-visitors" />
            <Stat icon={Eye} label="Celkem zobrazení stránek" value={stats.total_hits} color="#45AAF2" testId="stat-hits" />
            <Stat icon={TrendingUp} label="Průměr/den" value={Math.round(stats.total_hits / Math.max(1, stats.days))} color="#FF9F43" testId="stat-avg" />
          </div>

          <DailyChart data={stats.by_day || []} />

          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <h3 className="font-semibold text-[#2D3748] mb-4 flex items-center gap-2"><Eye size={18} className="text-[#FF9F43]" /> Nejnavštěvovanější stránky</h3>
            {stats.top_pages?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_pages.map((p, i) => {
                  const max = stats.top_pages[0].hits;
                  const w = (p.hits / max) * 100;
                  return (
                    <div key={p.path} className="flex items-center gap-3" data-testid={`top-page-${i}`}>
                      <div className="text-sm text-[#94A3B8] w-6 text-center">{i + 1}.</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#2D3748] truncate">{p.path}</div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                          <div className="h-full bg-gradient-to-r from-[#4ECDC4] to-[#95E1D3] rounded-full" style={{ width: `${w}%` }} />
                        </div>
                      </div>
                      <div className="text-sm font-bold text-[#2D3748] tabular-nums">{p.hits}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-[#64748B]">Zatím žádná data. Statistika začne sbírat data od chvíle, kdy někdo navštíví web.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsAdmin;
